/*
 * The wireframe-preview shape both Slide Templates and Presentation
 * Templates normalise their own manifest/`.cache` geometry into — a set of
 * placeholder rectangles, as percentages of the slide (0–100), so the
 * preview is resolution-independent and survives the JSON round-trip through
 * the dialog hand-off without carrying EMU-scale numbers. Each feature's own
 * normalisation (slideTemplatesApi.ts's `toPreviewBlock`,
 * presentationTemplatesApi.ts's `toValidPresentationSlidePreview`) reads a
 * different raw source shape, so only this leaf geometry — the type itself,
 * role parsing, and percentage rounding/clamping — is genuinely identical
 * and shared; re-exported under each feature's own name so neither feature's
 * public API changes.
 */

/** The placeholder roles either source emits, plus a catch-all. */
export type SlidePreviewRole = "title" | "body" | "image" | "chart" | "table" | "other";

export const KNOWN_PREVIEW_ROLES: readonly SlidePreviewRole[] = ["title", "body", "image", "chart", "table"];

export interface SlidePreviewBlock {
  readonly role: SlidePreviewRole;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Wireframe stand-in for a thumbnail image neither feature's manifest carries. */
export interface SlidePreview {
  /** Slide aspect ratio (width / height) — 1.777… for a 16:9 deck. */
  readonly aspect: number;
  readonly blocks: readonly SlidePreviewBlock[];
}

export function toPreviewRole(raw: unknown): SlidePreviewRole {
  if (typeof raw !== "string") return "other";
  const role = raw.toLowerCase();
  return KNOWN_PREVIEW_ROLES.find((known) => known === role) ?? "other";
}

/** Two decimals is ~0.2px of precision on a 200px-wide card — plenty. */
export function roundPct(value: number): number {
  return Math.round(value * 100) / 100;
}

export function clampPct(value: number): number {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}
